// Multi-select geography picker semantics. Callers hold a plain list of geography ids where EMPTY means
// the whole game; the picker shows that as an explicit, selected "Global (whole game)" option, mutually
// exclusive with specific geographies.
//
// Geographies nest (a sub-geography's countries are a subset of its parent's), and access is decided by
// country containment: a grant on Asia already covers South Asia and any sub-geography created under it
// later. So the picker keeps only the top-most choices - picking Asia includes its sub-geographies (shown
// as included, not separately tickable), while South Asia alone can still be picked on its own.
export const GLOBAL = '__global__';
export const GLOBAL_LABEL = 'Global (whole game)';
export const GLOBAL_DESCRIPTION = 'Every geography in this game, including ones added later';

/** What the picker shows as selected: the ids, or Global when there are none. */
export const toPickerValues = (geographyIds) =>
    (geographyIds && geographyIds.length ? geographyIds : [GLOBAL]);

/**
 * The geographies as a tree, flattened depth-first: each parent is followed by its sub-geographies,
 * siblings A-Z. A geography whose parent is not in the list (archived, say) is a root.
 * Each entry carries `depth` and `ancestors` (nearest first).
 */
export const geographyTree = (geographies) => {
    const list = geographies || [];
    const byId = new Map(list.map(g => [g.id, g]));
    const children = new Map();
    const roots = [];
    for (const g of list) {
        const parent = g.parentId && g.parentId !== g.id && byId.has(g.parentId) ? g.parentId : null;
        if (parent) {
            if (!children.has(parent)) children.set(parent, []);
            children.get(parent).push(g);
        } else {
            roots.push(g);
        }
    }
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
    const out = [];
    const seen = new Set();
    const walk = (g, ancestors) => {
        if (seen.has(g.id)) return; // a malformed cycle cannot recurse forever
        seen.add(g.id);
        out.push({ ...g, depth: ancestors.length, ancestors });
        (children.get(g.id) || []).sort(byName).forEach(c => walk(c, [g.id, ...ancestors]));
    };
    roots.sort(byName).forEach(g => walk(g, []));
    return out;
};

/** The selection without anything an ancestor in it already covers. */
export const topMost = (ids, tree) => {
    const selected = new Set(ids || []);
    const ancestorsOf = new Map((tree || []).map(g => [g.id, g.ancestors || []]));
    return (ids || []).filter(id => !(ancestorsOf.get(id) || []).some(a => selected.has(a)));
};

/**
 * The ids after the picker changed from `prevIds` to `next` (the picker's selected values).
 * Choosing Global (while geographies were selected) clears them; choosing a geography drops Global;
 * clearing everything is Global; choosing a parent folds its already-chosen sub-geographies into it.
 */
export const fromPickerChange = (prevIds, next, tree = []) => {
    const values = Array.isArray(next) ? next : [];
    const hadGeographies = (prevIds || []).length > 0;
    if (hadGeographies && values.includes(GLOBAL)) return [];
    return topMost(values.filter(v => v !== GLOBAL), tree);
};

const listNames = (names) => (names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/**
 * One line saying what the selection reaches, so the field explains itself: Global already includes every
 * geography (so there is nothing to add to it), and a chosen parent includes its sub-geographies.
 */
export const reachSummary = (ids, tree) => {
    const chosen = topMost(ids, tree);
    if (!chosen.length) {
        return 'Applies to the whole game, every geography included. Pick geographies to limit it.';
    }
    const byId = new Map((tree || []).map(g => [g.id, g]));
    const parts = chosen.map(id => {
        const name = byId.get(id)?.name || id;
        const inside = (tree || []).filter(g => (g.ancestors || []).includes(id)).map(g => g.name);
        if (!inside.length) return name;
        const shown = inside.length > 3 ? `${inside.slice(0, 3).join(', ')} +${inside.length - 3} more` : listNames(inside);
        return `${name} (including ${shown})`;
    });
    return `Applies to ${listNames(parts)}.`;
};

/**
 * Whether the menu should close after a pick: once Global is chosen, or once every geography is chosen or
 * included in a chosen one (nothing left to tick). Otherwise it stays open for the next pick.
 */
export const nothingLeftToPick = (ids, tree) => {
    if (!(ids || []).length) return true;
    const selected = new Set(ids);
    return (tree || []).every(g => selected.has(g.id) || (g.ancestors || []).some(a => selected.has(a)));
};

/**
 * Picker rows for the tree: indented by depth, and a sub-geography whose parent (or any ancestor) is
 * chosen is shown as included in it and cannot be ticked on its own.
 */
export const geographyOptions = (tree, selectedIds) => {
    const selected = new Set(selectedIds || []);
    const nameOf = new Map((tree || []).map(g => [g.id, g.name]));
    return (tree || []).map(g => {
        const coveredBy = (g.ancestors || []).find(a => selected.has(a));
        return {
            value: g.id,
            label: g.name,
            className: g.depth ? `geo-option geo-option--depth-${Math.min(g.depth, 4)}` : 'geo-option',
            ...(coveredBy ? { isDisabled: true, isChecked: true, description: `Included in ${nameOf.get(coveredBy)}` } : {}),
        };
    });
};
